import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CalendarCheck, BarChart3, UserX, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "sonner";
import { isoToDisplay } from "../components/DateInput";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
export const bulanOpts = BULAN.map((b, i) => ({ value: String(i + 1), label: b }));
export const tahunOpts = () => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2].map((v) => ({ value: String(v), label: String(v) })); };
export const fmtTgl = (t) => `${new Date(t + "T00:00:00").toLocaleDateString("id-ID", { weekday: "short" })}, ${isoToDisplay(t)}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function PresensiListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canWrite = can("presensi:write");
  const now = new Date();
  const [opts, setOpts] = useState({ jenis_ibadah: [], komisi: [] });
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [jenis, setJenis] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tanggal: today(), jenis_ibadah: "", komisi: "", keterangan: "" });
  const [jadwal, setJadwal] = useState(null);
  const [tidak, setTidak] = useState([]);
  useEffect(() => {
    if (!open || form.jenis_ibadah !== "Doa Persiapan Pelayan IBR" || !form.tanggal) return;
    setJadwal(null);
    api.get(`/jadwal/${form.tanggal}`).then((r) => { setJadwal(r.data.pelayan || []); setTidak(r.data.pelayan_tidak_ditemukan || []); }).catch(() => setJadwal([]));
  }, [open, form.jenis_ibadah, form.tanggal]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/presensi/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (bulan) params.bulan = bulan;
      if (tahun) params.tahun = tahun;
      if (jenis) params.jenis_ibadah = jenis;
      const r = await api.get("/presensi/sesi", { params });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [page, bulan, tahun, jenis]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => { const n = { ...f, [k]: v }; if (k === "jenis_ibadah" && v !== "Ibadah Komisi") n.komisi = ""; return n; });

  const create = async () => {
    if (!form.tanggal || !form.jenis_ibadah) { toast.error("Tanggal dan jenis ibadah wajib diisi"); return; }
    if (form.jenis_ibadah === "Ibadah Komisi" && !form.komisi) { toast.error("Komisi wajib dipilih untuk Ibadah Komisi"); return; }
    setSaving(true);
    try {
      const r = await api.post("/presensi/sesi", { ...form, komisi: form.komisi || null, keterangan: form.keterangan || null });
      toast.success("Sesi presensi dibuat. Semua jemaat berstatus Belum Dicatat.");
      setOpen(false);
      navigate(`/presensi/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="presensi-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Presensi</h1>
          <p className="text-sm text-slate-500 mt-1"><span className="font-semibold text-slate-700" data-testid="sesi-total">{data.total}</span> sesi ibadah</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => navigate("/presensi/rekap")} data-testid="goto-rekap-btn"><BarChart3 className="h-4 w-4 mr-2" />Rekap</Button>
          {can("jadwal:read") && <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => navigate("/presensi/rekap-pelayan")} data-testid="goto-rekap-pelayan-btn"><BarChart3 className="h-4 w-4 mr-2" />Rekap Pelayan</Button>}
          <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => navigate("/presensi/tidak-hadir")} data-testid="goto-tidak-hadir-btn"><UserX className="h-4 w-4 mr-2" />Tidak Hadir</Button>
          {canWrite && <Button className="rounded-xl min-h-[44px] bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={() => { setForm({ tanggal: today(), jenis_ibadah: "", komisi: "", keterangan: "" }); setOpen(true); }} data-testid="buat-presensi-btn"><Plus className="h-4 w-4 mr-2" />Buat Presensi</Button>}
        </div>
      </div>

      <Card className="p-3 rounded-2xl grid grid-cols-2 sm:grid-cols-3 gap-2">
        <SearchableSelect options={bulanOpts} value={BULAN[Number(bulan) - 1] || ""} onChange={(v) => { setBulan(v); setPage(1); }} placeholder="Bulan" testId="filter-bulan" />
        <SearchableSelect options={tahunOpts()} value={tahun} onChange={(v) => { setTahun(v); setPage(1); }} placeholder="Tahun" testId="filter-tahun" />
        <div className="col-span-2 sm:col-span-1"><SearchableSelect options={opts.jenis_ibadah} value={jenis} onChange={(v) => { setJenis(v); setPage(1); }} placeholder="Semua jenis ibadah" testId="filter-jenis" /></div>
      </Card>

      {loading ? <div className="text-center py-12 text-slate-400">Memuat...</div> : data.items.length === 0 ? (
        <Card className="p-10 text-center rounded-2xl" data-testid="empty-sesi"><CalendarCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" /><p className="text-slate-500">Belum ada sesi presensi pada periode ini.</p></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sesi-list">
          {data.items.map((s) => (
            <Card key={s.id} className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition-shadow border-slate-200/80" onClick={() => navigate(`/presensi/${s.id}`)} data-testid={`sesi-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-[hsl(224,64%,33%)] uppercase tracking-wide">{fmtTgl(s.tanggal)}</div>
                  <div className="font-bold text-slate-900 mt-0.5">{s.komisi || s.jenis_ibadah}</div>
                  {s.komisi && <div className="text-xs text-slate-500">{s.jenis_ibadah}</div>}
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${s.status === "Selesai" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{s.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <Stat label="Hadir" v={s.stats.hadir} cls="text-emerald-700" />
                <Stat label="Tdk Hadir" v={s.stats.tidak_hadir} cls="text-red-600" />
                <Stat label="Belum" v={s.stats.belum_dicatat} cls="text-slate-500" />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{s.stats.total_jemaat} jemaat</span>
                <span className="font-semibold text-slate-700">{s.stats.persentase_kehadiran}% hadir</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {data.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-slate-500">Hal. {page} / {data.total_pages}</span>
          <Button variant="outline" className="rounded-xl" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Buat Sesi Presensi</DialogTitle><DialogDescription>Semua jemaat akan berstatus "Belum Dicatat" sampai petugas mencatat kehadiran.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Tanggal <span className="text-red-500">*</span></Label><Input type="date" value={form.tanggal} onChange={(e) => setF("tanggal", e.target.value)} className="min-h-[48px] rounded-xl" data-testid="sesi-tanggal" /></div>
            <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Jenis Ibadah <span className="text-red-500">*</span></Label><SearchableSelect options={opts.jenis_ibadah} value={form.jenis_ibadah} onChange={(v) => setF("jenis_ibadah", v)} placeholder="Pilih jenis ibadah" testId="sesi-jenis" allowClear={false} /></div>
            {form.jenis_ibadah === "Ibadah Komisi" && (
              <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Komisi <span className="text-red-500">*</span></Label><SearchableSelect options={opts.komisi} value={form.komisi} onChange={(v) => setF("komisi", v)} placeholder="Pilih komisi" testId="sesi-komisi" allowClear={false} /></div>
            )}
            {form.jenis_ibadah === "Doa Persiapan Pelayan IBR" && (
              <div className="rounded-xl border bg-slate-50 p-3 text-sm" data-testid="sesi-pelayan-terjadwal">
                <div className="font-medium text-slate-700 mb-1">Pelayan terjadwal {form.tanggal ? isoToDisplay(form.tanggal) : ""}</div>
                {jadwal === null ? <span className="text-slate-500">Memuat jadwal...</span>
                  : jadwal.length === 0 ? <span className="text-amber-700">Belum ada Jadwal Pelayanan pada tanggal ini. Isi dulu di menu Jadwal Pelayanan.</span>
                  : <ul className="flex flex-wrap gap-1.5">{jadwal.map((n) => <li key={n} className={`px-2 py-0.5 rounded-full border ${tidak.includes(n) ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white text-slate-700"}`} data-testid="sesi-pelayan-item">{tidak.includes(n) ? "⚠ " : ""}{n}</li>)}</ul>}
                {tidak.length > 0 && <div className="text-xs text-amber-700 mt-2" data-testid="sesi-pelayan-warning">{tidak.length} nama tidak ditemukan di Data Jemaat dan tidak akan masuk daftar absensi.</div>}
              </div>
            )}
            <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Keterangan (opsional)</Label><Textarea value={form.keterangan} onChange={(e) => setF("keterangan", e.target.value)} className="rounded-xl" rows={2} data-testid="sesi-keterangan" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="rounded-xl bg-[hsl(224,64%,33%)]" onClick={create} disabled={saving} data-testid="simpan-sesi-btn">{saving ? "Membuat..." : "Buat & Mulai Catat"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, v, cls }) => (<div className="bg-slate-50 rounded-xl py-2"><div className={`text-lg font-bold ${cls}`}>{v}</div><div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div></div>);
